import {
  CallbackPayload,
  ConfigService,
  MessageBusService,
  RecordLocatorResponse,
  ResponseWrapper,
  SourceCredentialType,
  VaultConnectionOrgAuthorizeResponse,
} from '@shared-library';
import {inject} from '@angular/core';
import {map, Observable, of, switchMap} from 'rxjs';

export function StoreRecordLocatorResultsInVaultProfile(
  configService: ConfigService,
  rlsResponse: RecordLocatorResponse,
): {
  numDiscovered: number;
  numPending: number;
  numConnected: number;
} {
  const numDiscovered = Object.keys(rlsResponse.discovered_patient_accounts || {}).length;
  const numPending = Object.keys(rlsResponse.pending_patient_accounts || {}).length;
  const numConnected = Object.keys(rlsResponse.connected_patient_accounts || {}).length;

  for (const vaultProfileConnectionId of Object.keys(rlsResponse.discovered_patient_accounts || {})) {
    const discoveredFacility = rlsResponse.discovered_patient_accounts[vaultProfileConnectionId];
    configService.vaultProfileAddDiscoveredRecordLocatorAccount(discoveredFacility, vaultProfileConnectionId);
  }

  for (const vaultProfileConnectionId of Object.keys(rlsResponse.pending_patient_accounts || {})) {
    const pendingFacility = rlsResponse.pending_patient_accounts[vaultProfileConnectionId];
    configService.vaultProfileAddPendingRecordLocatorAccount(pendingFacility, vaultProfileConnectionId);
  }

  for (const vaultProfileConnectionId of Object.keys(rlsResponse.connected_patient_accounts || {})) {
    const connectedFacility = rlsResponse.connected_patient_accounts[vaultProfileConnectionId];
    configService.vaultProfileAddConnectedRecordLocatorAccount(connectedFacility, vaultProfileConnectionId);
  }

  return {
    numDiscovered,
    numPending,
    numConnected,
  };
}

export function FetchAndStoreRecordLocatorResultsInVaultProfile(
  configService: ConfigService,
  recordLocatorRegisterAndPollForStatus: () => Observable<ResponseWrapper>,
  recordLocatorResults: (taskId: string) => Observable<RecordLocatorResponse>,
): Observable<{
  numDiscovered: number;
  numPending: number;
  numConnected: number;
}> {
  return recordLocatorRegisterAndPollForStatus().pipe(
    switchMap((rlsStatusResponse: ResponseWrapper) => {
      if (rlsStatusResponse.data.status === 'success') {
        return recordLocatorResults(rlsStatusResponse.data.task_id);
      }

      return of(new RecordLocatorResponse());
    }),
    map((rlsResponse: RecordLocatorResponse) => {
      const totals = StoreRecordLocatorResultsInVaultProfile(configService, rlsResponse);
      configService.vaultProfileConfig = {
        rlsQueryComplete: true,
      };

      return totals;
    })
  );
}

// Helper to process TEFCA Direct authorization results and publish message bus events.
// - vaultConnectionIds: the list of ids we attempted to authorize
// - resp: response from backend; expected shape { successes: { [id]: { org_connection_id, connection_status, platform_type } }, failures: { [id]: { error, error_description } } }
export function ProcessTefcaDirectAuthorizationResults(
  vaultConnectionIds: string[],
  resp: VaultConnectionOrgAuthorizeResponse | null,
): void {
  const configService = inject(ConfigService);
  const messageBusService = inject(MessageBusService);

  const successes = resp?.successes || {};
  const failures = resp?.failures || {};

  // Build a quick lookup from vault_profile_connection_id -> external_state for pending/discovered accounts

  // For each attempted id, emit success/failure event and promote if applicable
  vaultConnectionIds.forEach((id) => {
    const successData = successes[id];
    const failureData = failures[id];

    if (successData) {
      const orgConnectionId = successData.org_connection_id ?? '';
      const connectionStatus = successData.connection_status ?? 'authorized';
      const platformType = successData.platform_type ?? 'tefca';
      const payload: CallbackPayload = {
        external_state: id,
        external_id: configService.systemConfig$.externalId,
        request_id: resp?.request_id,
        org_connection_id: orgConnectionId,
        connection_status: connectionStatus,
        platform_type: platformType,
        vault_profile_connection_id: id,
        patient_auth_type: SourceCredentialType.SourceCredentialTypeTefcaDirect,
        tefca_directory_id: successData.tefca_directory_id,
      };

      // Publish success event
      messageBusService.publishOrgConnectionComplete(payload);
      // Promote pending/discovered -> connected if we have an external_state
      configService.vaultProfileAuthorizeTefcaDirectConnectedAccount(
        id,
        orgConnectionId,
        connectionStatus,
        successData.tefca_directory_id,
      );
    } else if (failureData) {
      const error = failureData.error || 'fasten_server_error';
      const errorDescription = failureData.error_description || 'An unknown server error occurred: missing error type';
      const payload: CallbackPayload = {
        external_state: id,
        vault_profile_connection_id: id,
        external_id: configService.systemConfig$.externalId,
        request_id: resp?.request_id,
        error: error,
        error_description: errorDescription,
        patient_auth_type: SourceCredentialType.SourceCredentialTypeTefcaDirect,
      };

      messageBusService.publishOrgConnectionComplete(payload);
      // Do not promote on failure
      configService.vaultProfileRevokeTefcaDirectConnectedAccount(id);
    } else {
      // Catastrophic/malformed response: emit a generic failure per attempted id
      const payload: CallbackPayload = {
        external_state: id,
        vault_profile_connection_id: id,
        external_id: configService.systemConfig$.externalId,
        request_id: resp?.request_id,
        error: 'fasten_server_error',
        error_description: 'An unknown server error occurred: missing error type',
        patient_auth_type: SourceCredentialType.SourceCredentialTypeTefcaDirect,
      };
      messageBusService.publishOrgConnectionComplete(payload);
      configService.vaultProfileRevokeTefcaDirectConnectedAccount(id);
    }
  });
}
